/**
 * lib/permissions.ts
 * ------------------------------------------------------------------
 * Centralised role-based access control for the LKC Academic Platform.
 *
 * LKC RULES ENCODED HERE
 *  #1  Only `editor` (and admin/headteacher) may raise comment flags.
 *  #2  `hoy` enters the HOD/principal comment; `classTeacher` enters
 *      the single attendance+conduct comment.
 *  #3  `editor` may edit HOY + class-teacher comments on the report.
 *  #4  ONLY `admin` may generate final reports.
 *      `subjectTeacher` may NOT view final reports at all.
 *      `classTeacher` may view ONLY their own class, and ONLY after
 *      the batch is generated.
 * ------------------------------------------------------------------
 */
import type {
  Role,
  AppUser,
  Teacher,
  SchoolClass,
  Student,
  TermState,
} from '@/types';

export interface RoleMeta {
  id: Role;
  label: string;
  description: string;
  color: string; // tailwind classes for role badges
  priority: number; // lower = more powerful
}

export const ROLES: Record<Role, RoleMeta> = {
  admin: {
    id: 'admin',
    label: 'Administrator',
    description:
      'Full control: setup, users, term locking, and the only role that generates final reports.',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    priority: 1,
  },
  superAcademicAdmin: {
    id: 'superAcademicAdmin',
    label: 'Super Academic Admin',
    description:
      'An academic administrator who may also set up staff accounts — create a login, '
      + 'set academic, exams or admissions roles, and email the sign-in details. Never HR or '
      + 'finance roles, and never deleting accounts or resetting passwords. '
      + 'Grant it ALONGSIDE Academic Administrator, which is what carries their data access.',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    priority: 3,
  },
  systemAdmin: {
    id: 'systemAdmin',
    label: 'System Administrator',
    description: 'Full system-wide access and the sole manager of users and roles. Tightly restricted.',
    color: 'bg-slate-200 text-slate-800 border-slate-300',
    priority: 0,
  },
  staff: {
    id: 'staff',
    label: 'Staff (self-service)',
    description: 'Non-system staff. Sees only their own payslips, leave balance, and tax certificate via "My Pay & HR". No other access.',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    priority: 90,
  },
  academicAdmin: {
    id: 'academicAdmin',
    label: 'Academic Admin',
    description: 'Sees and edits everything in academics (setup, marks, reports, terms, curriculum). No HR/Finance/users.',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    priority: 2,
  },
  hrAdmin: {
    id: 'hrAdmin',
    label: 'HR Admin',
    description: 'Full HR including payroll and tax tables.',
    color: 'bg-violet-100 text-violet-800 border-violet-200',
    priority: 8,
  },
  hrOfficer: {
    id: 'hrOfficer',
    label: 'HR Officer',
    description: 'HR staff details only — not payroll or pay figures.',
    color: 'bg-violet-50 text-violet-700 border-violet-200',
    priority: 9,
  },
  payrollOfficer: {
    id: 'payrollOfficer',
    label: 'Payroll Officer',
    description: 'Payroll only — pay runs, payslips and payroll profiles.',
    color: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
    priority: 9,
  },
  financeAdmin: {
    id: 'financeAdmin',
    label: 'Finance Admin',
    description: 'Full finance: fee setup, billing, payments, statements, arrears and totals.',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    priority: 8,
  },
  feesOfficer: {
    id: 'feesOfficer',
    label: 'Fees Officer',
    description: 'Captures payments and views individual invoices/receipts — no aggregate totals.',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    priority: 10,
  },
  examsOfficer: {
    id: 'examsOfficer',
    label: 'Exams Officer',
    description: 'Runs exams: class subject lists, exam registers, and room seating plans. No other access.',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    priority: 10,
  },
  headteacher: {
    id: 'headteacher',
    label: 'Head of School',
    description: 'Full access to setup and reports; no user management.',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    priority: 2,
  },
  editor: {
    id: 'editor',
    label: 'Editor',
    description:
      'Reviews reports, flags errors for teachers, and edits HOY & class-teacher comments directly on the report.',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    priority: 3,
  },
  hoy: {
    id: 'hoy',
    label: 'Head of Year',
    description:
      'Enters the HOD comment on behalf of the principal for students in their year group(s).',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    priority: 4,
  },
  media: {
    id: 'media',
    label: 'Media',
    description:
      'Downloads pupils’ photographs, class by class, for the yearbook, ID cards and the website. Sees pupils’ names, classes and photos — nothing else.',
    color: 'bg-pink-100 text-pink-800 border-pink-200',
    priority: 6,
  },
  boardingMaster: {
    id: 'boardingMaster',
    label: 'Boarding Master',
    description:
      'Housemaster for a boarding house. Views the profiles and final reports of the boarders in their own house, and nobody else.',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    priority: 5,
  },
  classTeacher: {
    id: 'classTeacher',
    label: 'Class Teacher',
    description:
      'Enters one combined attendance & conduct comment. Views own class reports after generation.',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    priority: 5,
  },
  subjectTeacher: {
    id: 'subjectTeacher',
    label: 'Subject Teacher',
    description:
      'Enters marks for assigned subjects. Cannot view final reports.',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    priority: 6,
  },
  admissions: {
    id: 'admissions',
    label: 'Admissions Officer',
    description:
      'Office staff who manage student intake. Access to Admissions module only.',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    priority: 7,
  },
  parent: {
    id: 'parent',
    label: 'Parent / Guardian',
    description:
      'Read-only access to linked children: reports, attendance, profile.',
    color: 'bg-sky-100 text-sky-800 border-sky-200',
    priority: 8,
  },
  student: {
    id: 'student',
    label: 'Student',
    description:
      'Own study portal only: lessons, practice, homework and own report.',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    priority: 8,
  },
  hod: {
    id: 'hod',
    label: 'Head of Department',
    description:
      'Oversees a subject department. Views department analytics, teacher performance, and marks.',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    priority: 4,
  },
  hr: {
    id: 'hr',
    label: 'HR Officer',
    description:
      'Manages the HR module: staff records, compliance documents, clocking, leave, contracts, appraisals, and payroll export.',
    color: 'bg-rose-100 text-rose-800 border-rose-200',
    priority: 3,
  },
  bursar: {
    id: 'bursar',
    label: 'Bursar',
    description:
      'Manages the finance module: fee structures, billing runs, invoicing, payments, family statements, discount configuration, and arrears.',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    priority: 3,
  },
  bursar_assistant: {
    id: 'bursar_assistant',
    label: 'Bursar Assistant',
    description:
      'Day-to-day finance: capture payments, issue invoices, and view statements. Cannot change fee structures or discount configuration.',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    priority: 4,
  },
  operationsAdmin: {
    id: 'operationsAdmin',
    label: 'Operations Admin',
    description:
      'Runs admissions, HR (incl. payroll & tax tables) and finance (incl. fee config & totals), and manages the operational users under those domains. No academic or identity-admin access.',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    priority: 2,
  },
};

export interface Permissions {
  // Setup & settings
  canAccessSetup: boolean;
  canManageTeachers: boolean;
  canManageStudents: boolean;
  /** Create/manage families (sibling grouping + primary contact).
   *  Same custodians as student records: admin / headteacher /
   *  admissions. School-wide; consumed by comms + the finance module. */
  canManageFamilies: boolean;
  canManageSubjects: boolean;
  canManageClasses: boolean;
  canManageUsers: boolean;
  canManageTerms: boolean;
  canCloseTerms: boolean;
  canEditLockedTerm: boolean;
  /** Admin-only: configure CA counts/labels, exam titles, report notices. */
  canConfigureAssessments: boolean;

  // Marks
  canEnterMarks: boolean;
  canEditAnyMarks: boolean;
  canViewAllMarks: boolean;

  // Comments
  canEnterClassTeacherComment: boolean; // attendance+conduct (#2)
  canEnterHodComment: boolean; // HOY -> principal comment (#2)
  canEditCommentsOnReport: boolean; // editor live-edit (#3)

  // Flagging (#1)
  canRaiseFlags: boolean;
  canManageFlags: boolean;
  // Comment Review access. Editors edit; class teachers get read-only access
  // to print the editor's proof for their class. DERIVED — see getPermissions.
  canViewCommentReview: boolean;

  // Reports (#4)
  canPreviewReports: boolean; // live in-app report preview. DERIVED.
  canManageExams: boolean; // exam registers / seating / class lists. DERIVED.
  /** Set up an exam series pupils and parents see — which papers, who sits
   *  them, publishing it (Exams → Candidates, Internal exams). DERIVED from
   *  canManageExams: admin, academic admin, exams officer. Mirrored by
   *  canManageExamTimetables() in firestore.rules (from Al-Nur, 30 Oct 2026). */
  canManageExamTimetables: boolean;
  /** Open the Exams section: the exam timetable for every teacher. Seeing is
   *  not managing. DERIVED: teaching staff, and whoever manages exams. */
  canViewExams: boolean;
  /** Look up ANY class's, teacher's or room's timetable (the school, 31 Oct
   *  2026): admin, the head, academic admins, HODs and HOYs. Every other
   *  teacher sees their own timetable and the classes they teach or are class
   *  teacher of — My Timetable narrows its pickers to those. */
  canViewAllTimetables: boolean;
  /** Arrange cover for an absent teacher (Cover, lib/cover.ts; from Al-Nur,
   *  31 Oct 2026): admin, the head, academic admins and HODs. DERIVED.
   *  Mirrored by canManageCover() in firestore.rules. */
  canManageCover: boolean;
  /** Take a lesson register for ANY class, not only your own lessons:
   *  admin, the head, academic admins. DERIVED. */
  canEnterAllClassAttendance: boolean;
  /** Set next year's staffing (Teaching → Staffing): HODs for their own
   *  departments, leadership for all. DERIVED. Mirrored by ttStaffing rules. */
  canEditStaffing: boolean;
  /** Open Nako, the timetable editor, signed in from the portal (2 Nov 2026).
   *  The timetabling roles only, with NO canManageTerms fallback: the list is
   *  the server's (functions/nako.js), the rules' isTimetabler() and the
   *  solver's EDITOR_ROLES, and a link the server then refuses is worse than
   *  no link. DERIVED. */
  canOpenNako: boolean;
  canGenerateReports: boolean; // admin only
  canViewAnyReport: boolean;
  canViewOwnClassReports: boolean; // class teacher, post-generation
  /** Housemaster: final reports for the boarders in THEIR house only.
   *  Deliberately separate from canViewAnyReport — that one is read as
   *  "every class in the school" by GeneratedReports, and a housemaster
   *  supervising nineteen boys must not thereby see all 826 pupils. */
  canViewBoarderReports: boolean;
  canBulkSendReports: boolean;
  // Live, in-app academic view (View Reports) — the marks/grades pages a
  // teacher may watch assemble as marks are entered, for the classes they
  // teach. NOT the final booklet (no cover/back) and NOT batch-gated. DERIVED.
  canViewLiveAcademics: boolean;

  // Analytics / progress
  canViewProgress: boolean;
  canViewAllProgress: boolean;

  // Admin tooling
  canViewAudit: boolean;
  canViewActivityLog: boolean;
  canRunBackups: boolean;
  canBulkImport: boolean;

  // Admissions (Feature E)
  canAccessAdmissions: boolean;

  // Discipline (Step 2)
  canManageDiscipline: boolean;
  canManageAchievements: boolean; // record student achievements. DERIVED.

  // Parent portal (Phase 2)
  canAccessParentPortal: boolean;
  canManageParentSettings: boolean;
  /** Teacher → parent messaging (Part B). Granted to teaching roles
   *  (class/subject teacher, HOY, HOD) — NOT admin/head, who use the
   *  school-wide Parent Notices composer instead. */
  canMessageParents: boolean;
  /**
   * Read and answer the school's inbound mail.
   *
   * DELIBERATELY NOT canMessageParents, which class and subject teachers
   * hold. This is one mailbox carrying every family's correspondence —
   * fee disputes, bounces naming other children's addresses, replies
   * quoting what the school wrote. A teacher messaging their own class's
   * parents is a different thing entirely, and conflating the two would
   * hand the whole school's post to anyone who teaches.
   *
   * Kept identical to isMailStaff() in firestore.rules. If they drift,
   * the screen offers what the database refuses.
   */
  canReadSchoolMail: boolean;
  /**
   * See the photos on pupils' records — parents' uploads included — and take
   * a wrong one down. The office and senior staff for every pupil; a class
   * teacher for their own class, narrowed on screen and checked again on
   * the server when a photo is removed.
   */
  canReviewPupilPhotos: boolean;

  /** Open Photo Management and download pupils' photographs — one,
   *  several or a whole class. Admin and the media role only (Oct 2026).
   *  Removing photos and the parent reminders inside it are admin-only.
   *  Every download is written to the activity log. */
  canDownloadPupilPhotos: boolean;
  /**
   * See which parents the school has no working email address for, and fill
   * the gap in.
   *
   * A CLASS TEACHER IS NOT AN ADMINISTRATOR, and this is the one thing they
   * can change about a pupil's record. 156 families have never given LKC an
   * address, so they receive no reports, no invoices and no notices; nothing
   * in the portal will produce one, and the person who sees the child every
   * day is the person who can ask.
   *
   * Narrowed twice. On screen a class teacher sees only their own class,
   * while the office may choose any. On the server the same check runs
   * again, and the write is refused unless the address really is MISSING -
   * this permission fills a gap, it never overwrites an address that works.
   */
  canCollectParentContacts: boolean;
  /**
   * Open User Management in DELEGATED mode: create and manage accounts, but
   * only for roles this person may assign (see lib/roleAuthority), and only
   * for users whose every existing role they could have assigned.
   *
   * Deliberately separate from canManageUsers, which is full identity
   * management — deleting accounts, resetting passwords, changing email
   * addresses, rewriting claims. An academic admin sets up teachers; they do
   * not get the keys to everybody's account.
   */
  canManageTeacherAccounts: boolean;

  // HOD / Department analytics (Phase 3)
  canViewDepartmentAnalytics: boolean;

  // Curriculum (Phase 4)
  /** Manage scheme-of-work topics. HODs scoped to their own
   *  department(s); admin/headteacher see all. Parents view-only via
   *  the parent portal. */
  canManageCurriculum: boolean;

  /** §Study (ported from Al-Nur, TEACHING-AND-STUDENT-PORTAL-PLAN.md) — the
   *  teacher's side of the study portal: interactive lessons, homework,
   *  schemes, the study hub. Read-only over pupils' study data, and granted to
   *  the staff who may already enter marks or oversee a class, since it shows
   *  nothing they cannot already see about their own students. */
  canViewStudyProgress: boolean;
  /** The student portal itself — the `student` role's only grant. Nothing
   *  holds it yet: the role arrives with the rules sweep (plan, stage S0). */
  canAccessStudentPortal: boolean;

  /** Phase 5 — review applications submitted via the public form.
   *  Admin / headteacher / admissions only. Reviewers can update
   *  decisions (class, subjects) and admit or reject. */
  canReviewApplications: boolean;

  /** HR DOMAIN — access the HR module (staff, compliance, clocking,
   *  leave, contracts, appraisals, payroll export). Granted to the
   *  dedicated `hr` role and to `admin`. Invisible to everyone else,
   *  exactly like the academic module gates. */
  canAccessHR: boolean;

  /** Upload the clocking terminals' exports into hrAttendance, and link a
   *  terminal user to a staff record. The ONLY way punches enter the
   *  portal, and deliberately not an HR power: the school wants one named
   *  account to own it (Makoni's, via superAcademicAdmin) plus system
   *  admin. Tied to the ACCOUNT, not the person — if the holder leaves, the
   *  account is handed to their replacement (new email and password) and
   *  the upload and its weekly reminder move with it. */
  canImportClocking: boolean;

  /** See the Clocking screen, and nothing else in HR. Everyone in HR, plus
   *  whoever uploads the clocking (Nov 2026: Makoni's account was uploading
   *  what it could not then look at). Derived in getPermissions. */
  canViewClocking: boolean;

  /** HR PAYROLL ACCESS — the payroll subset of HR (pay runs, payslips,
   *  payroll profiles, severance). Split from canAccessHR so an HR
   *  officer can manage staff details without seeing pay, and a payroll
   *  officer can run pay without general HR. hrAdmin/admin get both. */
  canAccessPayroll: boolean;

  /** HR PAYROLL — manage the versioned Botswana PAYE tax tables
   *  (view/add/activate). Admin only; the bursar (hr) runs payroll but
   *  does not set statutory rates. */
  canManageTaxTables: boolean;

  /** FINANCE DOMAIN — access the fees/finance module (invoicing,
   *  payments, statements, arrears). Mirrors canAccessHR. Granted to
   *  admin, bursar, and bursar_assistant. */
  canAccessFinance: boolean;

  /** FINANCE CONFIG — manage fee structures, discount rules, and
   *  finance settings. Admin and bursar (not the assistant), like the
   *  payroll/tax-tables split. */
  canManageFinanceConfig: boolean;

  /** Issue or reverse a discount against an invoice (Nov 2026). The school named
   *  the bursar and operations admin; system admin as everywhere else. Set in
   *  getPermissions. Mirrors isDiscountIssuer in firestore.rules. */
  canIssueDiscounts: boolean;

  /** FINANCE TOTALS — see aggregate finance figures: the Overview KPIs,
   *  collection rates, arrears totals, billing‑run totals and whole‑
   *  family statement balances. A fees officer captures payments and
   *  views individual invoices/receipts but NOT these aggregates. */
  canViewFinanceTotals: boolean;

  /* ==================================================================
   * ACCOUNTING, PROCUREMENT AND RETAIL
   * ------------------------------------------------------------------
   * Declared ahead of the screens they gate, all defaulting to false. A flag
   * that arrives with its screen tends to arrive as whatever that screen
   * happened to need; declaring the set in one pass means the boundaries were
   * drawn by reading the school's separation of duties rather than one page's
   * requirements.
   * ================================================================== */

  /** FEE SPECIAL CASES — sign-off on a staff-child rate, a bursary or a
   *  prepayment. Deliberately NOT the bursar: the person who agrees the
   *  arrangement is not the person who approves it. */
  canApproveFeeCases: boolean;

  /** RETAIL OUTLET — operate an outlet (uniform shop, cafeteria): stock
   *  register, till, view day-end. Deliberately SEPARATE from the finance
   *  flags, so a till clerk can carry ONLY this and never see a family's
   *  fee account. */
  canOperateShop: boolean;

  /** OUTLET MANAGER — close and reopen the day-end drawer cash-up. The
   *  "clerk sells, manager closes" split, mirroring the finance
   *  access/config split: a clerk sells and sees the day-end but cannot
   *  CLOSE it. */
  canManageShop: boolean;

  /** FIXED ASSETS — the capital register: add/edit/dispose assets, manage
   *  depreciation categories and the capitalisation threshold. A closed
   *  financial year still blocks backdated edits. */
  canManageAssets: boolean;

  /** REQUISITIONS — raise a purchase requisition and act as a named
   *  approver. Broad (any staff): the requester names a colleague as
   *  approver, and review of every one at the procurement gate is the
   *  control. There is no separate approver role. */
  canRaiseRequisitions: boolean;

  /** REQUISITIONS MANAGER — the final-approval and procurement steps
   *  (purchase / receive / close, and reject at those gates). This is the
   *  single control on the whole workflow. */
  canManageRequisitions: boolean;

  /* ---- PAYMENT VOUCHERS ------------------------------------------
   * Separation of duties across roles that already exist:
   *   assistant PREPARES -> bursar CHECKS + RELEASES
   *     -> OPERATIONS MANAGER APPROVES, Head of School as second approver.
   *
   * TWO APPROVERS ON PURPOSE, confirmed by the school in September 2026.
   * An approver who has checked a voucher cannot then approve it — the
   * distinct-actor rule forbids it — so a SOLE approver who checks anything
   * creates a voucher nobody can pass. The second approver is what stops
   * that being a deadlock rather than a nicety.
   *
   * canApproveVouchers is withheld from bursar and bursar_assistant BY
   * DESIGN, on the same precedent as principal sign-off for fee cases. A
   * voucher is an instruction to move money; nobody signs off their own.
   *
   * Holding several of these flags is not the same as being able to walk a
   * voucher through alone. The three-way split is enforced per-voucher in the
   * workflow — a checker or approver who is also the preparer is refused —
   * not by withholding the capability. */

  /** Raise and edit a draft voucher, and submit it for checking. */
  canRaiseVouchers: boolean;
  /** Check a submitted voucher — coding, amount, payee — before approval. */
  canCheckVouchers: boolean;
  /** Sign-off: the authority to pay. Never the preparer or the checker. */
  canApproveVouchers: boolean;
  /** Build a payment batch, export the bank file, mark it paid. */
  canReleasePayments: boolean;

  /* ---- GENERAL LEDGER ----
   * Reading the books is broad — the Head of School and the bursar's
   * assistant both need it. CHANGING them is narrow. Posting a journal moves
   * the school's stated position, and the chart is the vocabulary every
   * posting depends on: a renumbering by the wrong hand silently misfiles
   * every future entry. */

  /** Create/rename/deactivate GL accounts and edit the posting map. */
  canManageChartOfAccounts: boolean;
  /** Post a manual journal, and reverse a posted one. */
  canPostJournals: boolean;
  /** Read the ledger: journals, trial balance, income & expenditure, balance sheet. */
  canViewGeneralLedger: boolean;
  /** Import a Pastel snapshot for the portal-vs-Pastel comparison. Pastel is
   *  the incumbent package here — pupils sit in it before they are enrolled —
   *  so that comparison is how the ledger cut-over gets proved. */
  canImportPastel: boolean;

  /** Maintain the supplier register AND its banking details. */
  canManageSuppliers: boolean;
}

const NONE: Permissions = {
  canAccessSetup: false,
  canManageTeachers: false,
  canManageStudents: false,
  canManageFamilies: false,
  canManageSubjects: false,
  canManageClasses: false,
  canManageUsers: false,
  canManageTerms: false,
  canCloseTerms: false,
  canEditLockedTerm: false,
  canConfigureAssessments: false,
  canEnterMarks: false,
  canEditAnyMarks: false,
  canViewAllMarks: false,
  canEnterClassTeacherComment: false,
  canEnterHodComment: false,
  canEditCommentsOnReport: false,
  canRaiseFlags: false,
  canManageFlags: false,
  canViewCommentReview: false,
  canPreviewReports: false,
  canManageExams: false,
  canManageExamTimetables: false,
  canViewExams: false,
  canViewAllTimetables: false,
  canManageCover: false,
  canEnterAllClassAttendance: false,
  canEditStaffing: false,
  canOpenNako: false,
  canGenerateReports: false,
  canViewAnyReport: false,
  canViewOwnClassReports: false,
  canViewBoarderReports: false,
  canBulkSendReports: false,
  canViewLiveAcademics: false,
  canViewProgress: false,
  canViewAllProgress: false,
  canViewAudit: false,
  canViewActivityLog: false,
  canRunBackups: false,
  canBulkImport: false,
  canAccessAdmissions: false,
  canManageDiscipline: false,
  canManageAchievements: false,
  canAccessParentPortal: false,
  canManageParentSettings: false,
  canMessageParents: false,
  canReadSchoolMail: false,
  canReviewPupilPhotos: false,
  canDownloadPupilPhotos: false,
  canCollectParentContacts: false,
  canManageTeacherAccounts: false,
  canViewDepartmentAnalytics: false,
  canManageCurriculum: false,
  canViewStudyProgress: false,
  canAccessStudentPortal: false,
  canReviewApplications: false,
  canAccessHR: false,
  canImportClocking: false,
  canViewClocking: false,
  canIssueDiscounts: false,
  canAccessPayroll: false,
  canManageTaxTables: false,
  canAccessFinance: false,
  canManageFinanceConfig: false,
  canViewFinanceTotals: false,
  canApproveFeeCases: false,
  canOperateShop: false,
  canManageShop: false,
  canManageAssets: false,
  canRaiseRequisitions: false,
  canManageRequisitions: false,
  canRaiseVouchers: false,
  canCheckVouchers: false,
  canApproveVouchers: false,
  canReleasePayments: false,
  canManageChartOfAccounts: false,
  canPostJournals: false,
  canViewGeneralLedger: false,
  canImportPastel: false,
  canManageSuppliers: false,
};

function basePermissions(role: Role | null | undefined): Permissions {
  switch (role) {
    case 'admin':
      return {
        ...NONE,
        canAccessSetup: true,
        canManageTeachers: true,
        canManageStudents: true,
        canManageFamilies: true,
        canManageSubjects: true,
        canManageClasses: true,
        canReviewPupilPhotos: true,
        canDownloadPupilPhotos: true,
        /* Inherited by ACADEMIC_ADMIN, which spreads this case - deliberate
           here: an academic admin chasing a class teacher for addresses
           needs to see the same list they do. */
        canCollectParentContacts: true,
        canReadSchoolMail: true,
        canManageTeacherAccounts: true,
        canManageUsers: true,
        canManageTerms: true,
        canCloseTerms: true,
        canEditLockedTerm: true,
        canConfigureAssessments: true,
        canEnterMarks: true,
        canEditAnyMarks: true,
        canViewAllMarks: true,
        canEnterClassTeacherComment: true,
        canEnterHodComment: true,
        canEditCommentsOnReport: true,
        canRaiseFlags: true,
        canManageFlags: true,
        canGenerateReports: true, // #4 — admin ONLY
        canViewAnyReport: true,
        canViewOwnClassReports: true,
        canBulkSendReports: true,
        canViewProgress: true,
        canViewAllProgress: true,
        canViewAudit: true,
        canViewActivityLog: true,
        canRunBackups: true,
        canBulkImport: true,
        canAccessAdmissions: true,
        canManageDiscipline: true,
        canManageParentSettings: true,
        canViewDepartmentAnalytics: true,
        canManageCurriculum: true,
        canViewStudyProgress: true,
        canReviewApplications: true,
        canAccessHR: true,
        canImportClocking: true,
        canAccessPayroll: true,
        canManageTaxTables: true,
        canAccessFinance: true,
        canManageFinanceConfig: true,
        canViewFinanceTotals: true,
        // The accounting layer, procurement and the outlets. Admin holds
        // every gate; the separation of duties that matters is enforced
        // per-voucher in the workflow, not by withholding flags here.
        canApproveFeeCases: true,
        canOperateShop: true,
        canManageShop: true,
        canManageAssets: true,
        canRaiseRequisitions: true,
        canManageRequisitions: true,
        canRaiseVouchers: true,
        canCheckVouchers: true,
        canApproveVouchers: true,
        canReleasePayments: true,
        canManageChartOfAccounts: true,
        canPostJournals: true,
        canViewGeneralLedger: true,
        canImportPastel: true,
        canManageSuppliers: true,
      };

    case 'headteacher':
      return {
        ...NONE,
        canReviewPupilPhotos: true,
        canCollectParentContacts: true,
        canReadSchoolMail: true,
        canAccessSetup: true,
        canManageTeachers: true,
        canManageStudents: true,
        canManageFamilies: true,
        canManageSubjects: true,
        canManageClasses: true,
        canManageUsers: false,
        canManageTerms: true,
        canCloseTerms: true,
        canEditLockedTerm: false,
        canEnterMarks: true,
        canEditAnyMarks: true,
        canViewAllMarks: true,
        canEnterClassTeacherComment: true,
        canEnterHodComment: true,
        canEditCommentsOnReport: true,
        canRaiseFlags: true,
        canManageFlags: true,
        canGenerateReports: false, // #4 — only admin generates
        canViewAnyReport: true,
        canViewOwnClassReports: true,
        canBulkSendReports: false,
        canViewProgress: true,
        canViewAllProgress: true,
        canViewAudit: true,
        canViewActivityLog: true,
        canRunBackups: false,
        canBulkImport: false,
        canAccessAdmissions: true,
        canManageDiscipline: true,
        canViewDepartmentAnalytics: true,
        canManageCurriculum: true,
        canViewStudyProgress: true,
        canReviewApplications: true,
        // Senior oversight: a Head of School can see and work across HR and
        // finance (staff, leave, payroll, invoices, payments, arrears).
        // Core config stays with the bursar/admin: canManageFinanceConfig
        // (fee structures/discounts) and canManageTaxTables remain false.
        canAccessHR: true,
        canAccessPayroll: true,
        canAccessFinance: true,
        canViewFinanceTotals: true,
        // Oversight and sign-off. Reads the books; posts nothing to them.
        // The two approvals that must not sit with the people who prepare
        // the work: fee special cases, and payment vouchers.
        canViewGeneralLedger: true,
        canApproveFeeCases: true,
        canApproveVouchers: true,
        canRaiseRequisitions: true,
        canManageRequisitions: true,
      };

    case 'editor':
      return {
        ...NONE,
        canViewAllMarks: true,
        // #1 flagging is an editor responsibility
        canRaiseFlags: true,
        canManageFlags: true,
        // #3 editors fix HOY + class-teacher comments on the report
        canEditCommentsOnReport: true,
        canViewAnyReport: true, // editors must read every report to review
        canViewProgress: true,
        canViewAllProgress: true,
        canRaiseRequisitions: true,   // any member of staff may request a purchase
      };

    case 'hoy':
      return {
        ...NONE,
        canViewStudyProgress: true,
        canViewAllMarks: true,
        canEnterHodComment: true,
        canRaiseFlags: true,
        canViewProgress: true,
        canViewAllProgress: true,
        canManageDiscipline: true,
        canMessageParents: true,
        canRaiseRequisitions: true,   // any member of staff may request a purchase
      };

    /* A housemaster stands in loco parentis for the boarders in their house:
       they answer for prep, conduct and results to parents who are not in
       Gaborone. So they see the FINAL REPORT, which a subject teacher never
       does — the scope is the house, not the classroom, and it is applied in
       canViewStudentProfile / boarderIsInMyHouse rather than here.

       Deliberately NOT granted: entering marks or comments. A housemaster
       reads the academic record, they do not write it. */
    case 'boardingMaster':
      return {
        ...NONE,
        canViewProgress: true,
        /* Reads the final report — the thing a subject teacher never sees.
           NOT canViewAnyReport: GeneratedReports reads that as "every class
           in the school", so granting it to a housemaster with nineteen boys
           would hand them all 826 pupils. Scope is the house, enforced in
           boarderIsInMyHouse. */
        canViewBoarderReports: true,
        canManageDiscipline: true,
        canMessageParents: true,
        canRaiseRequisitions: true,   // any member of staff may request a purchase
      };

    case 'hod':
      return {
        ...NONE,
        canViewAllMarks: true,
        canViewProgress: true,
        canViewAllProgress: true,
        canViewDepartmentAnalytics: true,
        canManageDiscipline: true,
        canManageCurriculum: true,
        canViewStudyProgress: true,
        canMessageParents: true,
        canRaiseRequisitions: true,   // any member of staff may request a purchase
      };

    case 'classTeacher':
      return {
        ...NONE,
        canReviewPupilPhotos: true,
        /* Their own class only - narrowed on screen and checked again on the
           server, which also refuses to overwrite an address that works. */
        canCollectParentContacts: true,
        canEnterMarks: true,
        canViewAllMarks: true,
        canEnterClassTeacherComment: true,
        canViewOwnClassReports: true,
        canViewStudyProgress: true,   // lessons, homework, schemes — their own classes (contextual)
        // Cross-subject analytics (Progress / Teacher-Dept Analysis) are a
        // supervisor/manager view — class teachers use Entry + the class
        // comment + own-class reports for their pastoral oversight.
        canManageDiscipline: true,
        canMessageParents: true,
        canRaiseRequisitions: true,   // any member of staff may request a purchase
      };

    case 'subjectTeacher':
      return {
        ...NONE,
        canEnterMarks: true, // assigned subjects only (contextual)
        canViewAllMarks: false,
        // #4 — subject teachers CANNOT view final reports at all, and
        // CANNOT see cross-class/department analytics (supervisor view).
        canViewProgress: false,
        canViewStudyProgress: true,   // lessons, homework, schemes — assigned subjects only (contextual)
        canMessageParents: true,
        canRaiseRequisitions: true,   // any member of staff may request a purchase
      };

    case 'admissions':
      return {
        ...NONE,
        canReviewPupilPhotos: true,
        canReadSchoolMail: true,
        canAccessAdmissions: true,
        canReviewApplications: true,
        canManageFamilies: true,
        /* Report cards, every class, to READ (the school, 27 Oct 2026):
           admissions answer families and receiving schools about a pupil's
           results. Not the comments editor — that is canEditCommentsOnReport,
           which they do not get. */
        canViewAnyReport: true,
      };

    case 'parent':
      return {
        ...NONE,
        canAccessParentPortal: true,
      };

    case 'hr':
      return {
        ...NONE,
        canAccessHR: true,
        canAccessPayroll: true,
      };

    case 'bursar':
      return {
        ...NONE,
        canReadSchoolMail: true,
        canAccessFinance: true,
        canManageFinanceConfig: true,
        canViewFinanceTotals: true,
        // Accounts owns the books, procurement, the outlets and the
        // capital register. Vouchers: prepares, checks and releases —
        // but does NOT approve. Sign-off on money leaving the school is
        // the Head's, and that is the control.
        canRaiseRequisitions: true,
        canManageRequisitions: true,
        canManageAssets: true,
        canOperateShop: true,
        canManageShop: true,
        canRaiseVouchers: true,
        canCheckVouchers: true,
        canReleasePayments: true,
        canManageSuppliers: true,
        canManageChartOfAccounts: true,
        canPostJournals: true,
        canViewGeneralLedger: true,
        canImportPastel: true,
      };

    case 'bursar_assistant':
      return {
        ...NONE,
        canReadSchoolMail: true,
        canAccessFinance: true,
        // Reads the books; posts nothing to them.
        canViewGeneralLedger: true,
        canRaiseRequisitions: true,
        canManageRequisitions: true,
        canOperateShop: true,
        // Prepares vouchers only — cannot check their own work, cannot approve.
        canRaiseVouchers: true,
      };

    default:
      return { ...NONE };
  }
}

/* ------------------------------------------------------------------
 * New role catalog (composed from the base roles above). Multi-role:
 * a user's effective permissions are the union of all their roles.
 * ---------------------------------------------------------------- */
const ACADEMIC_ADMIN: Permissions = {
  ...basePermissions('admin'),
  canManageUsers: false,       // identity management is System Admin only
  /* EXPLICIT, because this object is built from the admin's grid and would
     otherwise INHERIT the power — which would hand account creation to all
     ten academic administrators, the exact opposite of what the school
     asked for. superAcademicAdmin turns it back on for the one person. */
  canManageTeacherAccounts: false,
  canDownloadPupilPhotos: false, // admin and media only — not every academic admin
  canManageExams: true,        // academic admins run exams too
  canIssueDiscounts: false,
  canAccessHR: false,
  canImportClocking: false,
  canViewClocking: false,
  canAccessPayroll: false,
  canManageTaxTables: false,
  canAccessFinance: false,
  canManageFinanceConfig: false,
  canViewFinanceTotals: false,
  /* Academics only. Every accounting, procurement and retail capability is
     switched off explicitly, because this object is built by spreading the
     ADMIN set and subtracting — so a flag granted to admin arrives here
     silently unless it is named. An academic administrator running the
     timetable has no business posting a journal or releasing a payment. */
  canApproveFeeCases: false,
  canOperateShop: false,
  canManageShop: false,
  canManageAssets: false,
  canRaiseRequisitions: false,
  canManageRequisitions: false,
  canRaiseVouchers: false,
  canCheckVouchers: false,
  canApproveVouchers: false,
  canReleasePayments: false,
  canManageChartOfAccounts: false,
  canPostJournals: false,
  canViewGeneralLedger: false,
  canImportPastel: false,
  canManageSuppliers: false,
};

const OPERATIONS_ADMIN: Permissions = {
  ...NONE,
  canReviewPupilPhotos: true,
  canCollectParentContacts: true,
  canReadSchoolMail: true,
  // Admissions + applications
  canAccessAdmissions: true,
  canReviewApplications: true,
  canManageFamilies: true,
  // HR (full, incl. payroll + tax tables)
  canAccessHR: true,
  canAccessPayroll: true,
  canManageTaxTables: true,
  // Finance (full, incl. config + totals)
  canAccessFinance: true,
  canManageFinanceConfig: true,
  canViewFinanceTotals: true,
  // NOTE: no canManageUsers here — delegated user management for the
  // operational worker roles is gated separately (see roleAuthority.ts
  // + the delegated user-management screen), not via this flag.
  /* The books, procurement, the outlets and the capital register.
     Operations sits above finance admin and carries every voucher gate —
     raise, check, approve, release. That does NOT let one person walk a
     voucher through alone: the three-way separation is enforced per-voucher
     in the workflow, so holding all four means this role may play ANY part,
     never two parts on the same voucher.
     Fee-case sign-off is withheld — that stays with the Head of School. */
  canOperateShop: true,
  canManageShop: true,
  canManageAssets: true,
  canRaiseRequisitions: true,
  canManageRequisitions: true,
  canRaiseVouchers: true,
  canCheckVouchers: true,
  canApproveVouchers: true,
  canReleasePayments: true,
  canManageChartOfAccounts: true,
  canPostJournals: true,
  canViewGeneralLedger: true,
  canImportPastel: true,
  canManageSuppliers: true,
};

function permissionsForRole(role: Role | null | undefined): Permissions {
  switch (role) {
    case 'systemAdmin':
      return basePermissions('admin');               // full system access
    case 'academicAdmin':
      return ACADEMIC_ADMIN;
    /* An academic administrator who ALSO sets up staff accounts. Held by one
       person by deliberate choice: the school wanted this for Makoni, not for
       all ten academic administrators, and permissions here come from roles
       rather than from individuals — so the way to give it to one person is
       a role only that person holds.

       Grant it alongside academicAdmin. The Firestore and Storage rules name
       academicAdmin in their role lists; this role is about what the app
       lets somebody DO, not about what the database lets them read. */
    case 'superAcademicAdmin':
      return { ...ACADEMIC_ADMIN, canManageTeacherAccounts: true, canImportClocking: true };
    case 'media':
      // Opens Photo Management (canReviewPupilPhotos) for its Photos tab;
      // the Uploads tab and the photo log stay with the office.
      return { ...NONE, canReviewPupilPhotos: true, canDownloadPupilPhotos: true };
    /* A pupil sees their own study portal and nothing else. What it shows is
       static syllabus content or their own work and report (getMyAcademics),
       so this single flag is the whole grant — no roster, no other pupil. */
    case 'student':
      return { ...NONE, canAccessStudentPortal: true };
    case 'operationsAdmin':
      return OPERATIONS_ADMIN;
    case 'hrAdmin':
      return { ...NONE, canAccessHR: true, canAccessPayroll: true, canManageTaxTables: true };
    case 'hrOfficer':
      return { ...NONE, canAccessHR: true };
    case 'payrollOfficer':
      return { ...NONE, canAccessPayroll: true };
    case 'financeAdmin':
      // The bursar's standing exactly: prepares, checks and releases
      // vouchers, never approves them.
      return {
        ...NONE,
        canReadSchoolMail: true,
        canAccessFinance: true, canManageFinanceConfig: true, canViewFinanceTotals: true,
        canRaiseRequisitions: true,
        canManageRequisitions: true,
        canManageAssets: true,
        canOperateShop: true,
        canManageShop: true,
        canRaiseVouchers: true,
        canCheckVouchers: true,
        canReleasePayments: true,
        canManageSuppliers: true,
        canManageChartOfAccounts: true,
        canPostJournals: true,
        canViewGeneralLedger: true,
        canImportPastel: true,
      };
    case 'feesOfficer':
      // Fees, plus requesting a purchase and serving at an outlet till.
      // No config, no totals.
      return { ...NONE,
        canReadSchoolMail: true, canAccessFinance: true, canRaiseRequisitions: true, canOperateShop: true };
    case 'examsOfficer':
      return { ...NONE, canManageExams: true };
    default:
      return basePermissions(role);
  }
}

/** OR two permission sets together (true wins). */
function unionPerms(a: Permissions, b: Permissions): Permissions {
  const out = {} as Permissions;
  (Object.keys(a) as (keyof Permissions)[]).forEach((k) => { out[k] = a[k] || b[k]; });
  return out;
}

/**
 * Effective permissions for a single role OR a set of roles (multi-role
 * users). The result is the union — any role granting a capability wins.
 */
export function getPermissions(role: Role | Role[] | null | undefined): Permissions {
  const base = Array.isArray(role)
    ? role.length === 0
      ? { ...NONE }
      : role.map(permissionsForRole).reduce(unionPerms, { ...NONE })
    : permissionsForRole(role);
  // Derived: Comment Review is reachable by editors (who edit) and by class
  // teachers (read-only, to print the editor's proof for their class).
  base.canViewCommentReview = base.canRaiseFlags || base.canEnterClassTeacherComment;
  // Derived: anyone who can generate reports or edit report comments may use
  // the live in-app report preview.
  base.canPreviewReports = base.canGenerateReports || base.canEditCommentsOnReport;
  // Derived: every teaching/report role may open the live academic view for
  // their own classes (subject teachers included) — mark-enterers and anyone
  // who can already see reports. Scoped per-class by getAccessibleClasses at
  // the call site, so a subject teacher only reaches classes they teach.
  base.canViewLiveAcademics =
    base.canEnterMarks ||
    base.canViewOwnClassReports ||
    base.canViewAnyReport ||
    base.canEditCommentsOnReport;
  // Exam running (registers, seating, class lists) belongs to the Exams
  // Officer role (explicit grant, preserved here) plus admin as superuser.
  base.canManageExams = base.canManageExams || base.canGenerateReports;
  base.canManageExamTimetables = base.canManageExamTimetables || base.canManageExams;
  base.canViewExams = base.canViewExams || base.canManageExamTimetables || base.canViewStudyProgress;
  {
    const held = (Array.isArray(role) ? role : role ? [role] : []) as string[];
    base.canViewAllTimetables = base.canViewAllTimetables || base.canManageTerms
      || held.some((r) => ['admin', 'systemAdmin', 'academicAdmin', 'superAcademicAdmin', 'headteacher', 'hod', 'hoy'].includes(r));
    base.canManageCover = base.canManageCover || base.canManageTerms
      || held.some((r) => ['admin', 'systemAdmin', 'academicAdmin', 'superAcademicAdmin', 'headteacher', 'hod'].includes(r));
    base.canEditStaffing = base.canEditStaffing || base.canManageTerms
      || held.some((r) => ['admin', 'systemAdmin', 'academicAdmin', 'superAcademicAdmin', 'headteacher', 'hod'].includes(r));
    base.canEnterAllClassAttendance = base.canEnterAllClassAttendance || base.canManageTerms
      || held.some((r) => ['admin', 'systemAdmin', 'academicAdmin', 'superAcademicAdmin', 'headteacher'].includes(r));
    base.canOpenNako = held.some((r) => ['admin', 'systemAdmin', 'academicAdmin', 'superAcademicAdmin'].includes(r));
    base.canIssueDiscounts = held.some((r) => ['admin', 'systemAdmin', 'bursar', 'operationsAdmin'].includes(r));
  }
  // Achievements are recorded by the same staff who record discipline —
  // the positive counterpart, so parents don't only hear about problems.
  base.canManageAchievements = base.canManageDiscipline;
  base.canViewClocking = base.canAccessHR || base.canImportClocking;
  return base;
}

export function hasPermission(
  role: Role | null | undefined,
  key: keyof Permissions,
): boolean {
  return getPermissions(role)[key];
}

/* ------------------------------------------------------------------
   Contextual helpers
   ------------------------------------------------------------------ */

export function getLinkedTeacher(
  user: AppUser | null | undefined,
  teachers: Teacher[],
): Teacher | null {
  if (!user || !teachers?.length) return null;
  if (user.linkedTeacherId) {
    return teachers.find((t) => t.id === user.linkedTeacherId) ?? null;
  }
  if (user.email) {
    return (
      teachers.find(
        (t) => t.email?.toLowerCase() === user.email.toLowerCase(),
      ) ?? null
    );
  }
  return null;
}

const PRIVILEGED: Role[] = ['admin', 'headteacher'];

/* ------------------------------------------------------------------
 * Multi-role helpers for contextual checks. A user's effective roles
 * are roles[] if present, else [role]. These make the contextual
 * academic functions consistent with the union permission model, so a
 * person who is BOTH (say) a subjectTeacher and an academicAdmin is
 * recognised by every check, not just whichever single value sits in
 * `role`.
 * ---------------------------------------------------------------- */
function effectiveRoles(user: AppUser | null | undefined): Role[] {
  if (!user) return [];
  return user.roles && user.roles.length ? user.roles : (user.role ? [user.role] : []);
}
function userHasRole(user: AppUser | null | undefined, role: Role): boolean {
  return effectiveRoles(user).includes(role);
}
function userHasAnyRole(user: AppUser | null | undefined, roles: Role[]): boolean {
  const er = effectiveRoles(user);
  return roles.some((r) => er.includes(r));
}
/** Privileged = holds admin or headteacher among any of their roles. */
function isPrivileged(user: AppUser | null | undefined): boolean {
  return userHasAnyRole(user, PRIVILEGED);
}

/** Can this user enter/edit a mark for this student & subject? */
export function canEnterMarksFor(
  user: AppUser | null,
  student: Student | null,
  subjectId: string | null,
  teachers: Teacher[],
): boolean {
  if (!user || !student || !subjectId) return false;
  if (isPrivileged(user)) return true;

  const t = getLinkedTeacher(user, teachers);
  if (!t) return false;

  // EDIT rights for a subject mark come ONLY from being that
  // student's assigned subject teacher. Being the class teacher
  // does NOT, by itself, let you edit marks for a subject another
  // teacher owns (e.g. a split ICT group). The class teacher's
  // whole-class visibility is handled separately (read-only) via
  // canViewMarksFor — editing stays with the subject owner.
  if (student.subjectTeachers?.[subjectId] === t.id) return true;

  // Fallback for legacy data: if the student has NO subject
  // teacher recorded for this subject at all, fall back to the
  // class-teacher relationship so marks aren't un-enterable on
  // un-migrated data. Once subjectTeachers is set (post-S1),
  // the strict rule above governs.
  const noOwner = !student.subjectTeachers?.[subjectId];
  if (noOwner && userHasRole(user, 'classTeacher')) {
    if (t.classTeacherOf === student.currentClass) return true;
    if (t.assistantClassTeacherOf?.includes(student.currentClass))
      return true;
  }
  return false;
}

/**
 * Can this user SEE this student's row for a subject (even if
 * read-only)? A class teacher legitimately needs to see their
 * whole class's progress for every subject, including groups
 * taught by other teachers — but read-only (see canEnterMarksFor
 * for who may EDIT). Subject teachers only see their own group.
 */
export function canViewMarksFor(
  user: AppUser | null,
  student: Student | null,
  subjectId: string | null,
  teachers: Teacher[],
): boolean {
  if (!user || !student || !subjectId) return false;
  if (isPrivileged(user)) return true;
  // anyone who may edit may obviously view
  if (canEnterMarksFor(user, student, subjectId, teachers))
    return true;

  const t = getLinkedTeacher(user, teachers);
  if (!t) return false;

  // Class teacher of this student's class sees the whole class
  // for any subject (read-only for groups they don't teach).
  if (
    t.classTeacherOf === student.currentClass ||
    t.assistantClassTeacherOf?.includes(student.currentClass)
  ) {
    return true;
  }
  return false;
}

/**
 * Can this user view the Student Profile page for a given student?
 * Recommendation locked in with Simon: admin/headteacher always;
 * class teachers (incl. assistant) for students in their own class.
 * Subject teachers are deliberately excluded — viewing a student's
 * full cross-subject academic history is a privacy expansion the
 * project hasn't authorised. If LKC later wants to widen this,
 * change one function and tests will tell us what else moves.
 */
export function canViewStudentProfile(
  user: AppUser | null,
  student: Student | null,
  teachers: Teacher[],
): boolean {
  if (!user || !student) return false;
  if (isPrivileged(user)) return true;
  // Academic administrators (canManageStudents) AND finance staff can view
  // every student profile: finance (bursar, bursar_assistant, financeAdmin,
  // feesOfficer) invoice students and bill families, so they must be able to
  // search for and open any pupil — otherwise the dashboard search finds
  // nothing for them (canAccessFinance is the shared finance-staff signal).
  const perms = getPermissions(user.roles ?? user.role);
  if (perms.canManageStudents || perms.canAccessFinance) return true;
  // A housemaster may open any boarder in their own house — and no one else,
  // including day scholars and boarders in the other house.
  if (boarderIsInMyHouse(user, student)) return true;
  const t = getLinkedTeacher(user, teachers);
  if (!t) return false;
  if (
    t.classTeacherOf === student.currentClass ||
    t.assistantClassTeacherOf?.includes(student.currentClass)
  ) {
    return true;
  }
  return false;
}

/**
 * Is this pupil a boarder in a house this user is housemaster of?
 *
 * BOTH halves are required, and each is load-bearing:
 *   - the user must actually hold the boardingMaster role, and
 *   - the pupil must be `boarding` AND in one of the user's houses.
 *
 * A boardingMaster with no houses listed supervises nobody. That is the safe
 * default: the alternative — treating "no houses" as "all houses" — hands a
 * newly created account every boarder's report the moment the role is
 * granted and before anyone has said which house they run.
 *
 * A pupil with no `boardingHouse` matches nobody either, for the same
 * reason: an unassigned boarder is not everyone's to read.
 */
export function boarderIsInMyHouse(user: AppUser | null, student: Student | null): boolean {
  if (!user || !student) return false;
  const roles = user.roles ?? (user.role ? [user.role] : []);
  if (!roles.includes('boardingMaster')) return false;
  if (student.admissions?.boardingStatus !== 'boarding') return false;
  const house = student.admissions?.boardingHouse;
  if (!house) return false;
  return (user.boardingHouses ?? []).includes(house);
}

/** Every boarder a housemaster is responsible for. Empty for anyone else. */
export function myBoarders(user: AppUser | null, students: Student[]): Student[] {
  if (!user) return [];
  const roles = user.roles ?? (user.role ? [user.role] : []);
  if (!roles.includes('boardingMaster')) return [];
  return students.filter((s) => boarderIsInMyHouse(user, s));
}

/** Can this class teacher enter the attendance+conduct comment (#2)? */
export function canEnterClassCommentFor(
  user: AppUser | null,
  classId: string,
  classes: SchoolClass[],
  teachers: Teacher[],
): boolean {
  if (!user || !classId) return false;
  if (isPrivileged(user)) return true;
  // Academic / system admins oversee attendance & class comments school-wide.
  if (userHasAnyRole(user, ['academicAdmin', 'systemAdmin'])) return true;

  // Otherwise: whoever is ASSIGNED as this class's (assistant) class teacher —
  // regardless of whether their account also carries the 'classTeacher' role
  // string. The assignment ON THE CLASS is the authority. This fixes assigned
  // class teachers whose accounts are stored as subjectTeacher/hod (e.g. the
  // AS Jan A class teacher), who were wrongly blocked by the old role gate.
  const t = getLinkedTeacher(user, teachers);
  const cls = classes.find((c) => c.id === classId);
  if (!t || !cls) return false;
  return cls.classTeacherId === t.id || cls.assistantClassTeacherId === t.id;
}

/** Can this HOY enter the HOD comment for this student's year (#2)? */
export function canEnterHodCommentFor(
  user: AppUser | null,
  student: Student | null,
  classes: SchoolClass[],
): boolean {
  if (!user || !student) return false;
  if (isPrivileged(user)) return true;
  if (!userHasRole(user, 'hoy')) return false;

  const cls = classes.find((c) => c.id === student.currentClass);
  if (!cls) return false;
  // If the HOY has no explicit forms, they cover all forms.
  if (!user.hoyForms || user.hoyForms.length === 0) return true;
  return user.hoyForms.includes(String(cls.form));
}

/**
 * Can this user view the generated final report for a class? (#4)
 *  - admin / headteacher / editor: any class
 *  - classTeacher: only own class, and only if batch is ready
 *  - subjectTeacher / hoy: never
 */
export function canViewClassReports(
  user: AppUser | null,
  classId: string,
  classes: SchoolClass[],
  teachers: Teacher[],
  batchReady: boolean,
): boolean {
  if (!user || !classId) return false;
  if (isPrivileged(user)) return true;
  // Admissions read every class's report cards (27 Oct 2026) — read only.
  if (userHasRole(user, 'admissions')) return true;
  if (userHasRole(user, 'editor')) {
    // A form-scoped editor sees only their form's classes; an unscoped
    // editor (no editorForms) keeps school-wide access (backward compat).
    if (!user.editorForms?.length) return true;
    const cls = classes.find((c) => c.id === classId);
    return !!cls && user.editorForms.includes(String(cls.form));
  }
  if (!userHasRole(user, 'classTeacher')) return false;
  if (!batchReady) return false;

  const t = getLinkedTeacher(user, teachers);
  const cls = classes.find((c) => c.id === classId);
  if (!t || !cls) return false;
  return cls.classTeacherId === t.id || cls.assistantClassTeacherId === t.id;
}

/** Classes this user is allowed to see in pickers. */
export function getAccessibleClasses(
  user: AppUser | null,
  classes: SchoolClass[],
  teachers: Teacher[],
  students: Student[],
): SchoolClass[] {
  if (!user || !classes?.length) return [];
  // Archived classes (e.g. the Alumni / transferred+completed holding class)
  // never appear in academic pickers — analysis, reports, marks, etc.
  classes = classes.filter((c) => !c.archived);
  if (isPrivileged(user)) return classes;
  if (userHasRole(user, 'editor')) {
    // Form-scoped editor -> only their form's classes; unscoped -> all.
    if (!user.editorForms?.length) return classes;
    return classes.filter((c) => user.editorForms!.includes(String(c.form)));
  }

  if (userHasRole(user, 'hoy')) {
    if (!user.hoyForms?.length) return classes;
    return classes.filter((c) => user.hoyForms!.includes(String(c.form)));
  }

  // HODs oversee their department across the WHOLE school — every class/form,
  // not just the ones they personally teach.
  if (userHasRole(user, 'hod')) return classes;

  const t = getLinkedTeacher(user, teachers);
  if (!t) return [];

  const ids = new Set<string>();
  if (t.classTeacherOf) ids.add(t.classTeacherOf);
  t.assistantClassTeacherOf?.forEach((c) => ids.add(c));

  // Prefer the PRECISE class-level link (classAssignments:
  // subjectId -> classIds, from the S1 fix) — "classes I actually
  // teach". Only fall back to the broad form-level expansion for
  // legacy teachers who have teachingAssignments but no
  // classAssignments, so old data still works.
  const hasPreciseLinks =
    t.classAssignments &&
    Object.values(t.classAssignments).some((arr) => arr?.length);
  if (hasPreciseLinks) {
    Object.values(t.classAssignments!).forEach((clsIds) =>
      clsIds?.forEach((cid) => ids.add(cid)),
    );
  } else {
    Object.keys(t.teachingAssignments ?? {}).forEach((form) => {
      classes
        .filter((c) => String(c.form) === form)
        .forEach((c) => ids.add(c.id));
    });
  }

  // Safety net: classes where a student explicitly lists this
  // teacher as their subject teacher (never hide a class the
  // teacher is genuinely assigned a student in).
  students.forEach((s) => {
    if (Object.values(s.subjectTeachers ?? {}).includes(t.id)) {
      ids.add(s.currentClass);
    }
  });
  return classes.filter((c) => ids.has(c.id));
}

/** Term editability: locked/closed terms editable only by admin. */
export function isTermEditable(
  state: TermState | undefined,
  role: Role | null | undefined,
): boolean {
  if (state === 'closed' || state === 'locked') return role === 'admin';
  return true;
}

/* ------------------------------------------------------------------
   Curriculum visibility (Phase 4)
   ------------------------------------------------------------------ */

/**
 * Which departments may this user manage curriculum for?
 *
 *  - admin / headteacher: every department (returns null → "all").
 *  - hod: scoped to the departments listed on AppUser.hodDepartments.
 *         If hodDepartments is missing/empty we fall back to the
 *         department on the linked Teacher record (best-effort).
 *  - anyone else: returns [] (no department).
 *
 * `null` is the wildcard ("see everything"); an empty array means
 * "explicitly nothing". Callers should treat them differently.
 */
export function curriculumDepartmentsFor(
  user: AppUser | null | undefined,
  teachers: Teacher[],
): string[] | null {
  if (!user) return [];
  if (userHasAnyRole(user, ['admin', 'headteacher'])) return null;
  if (!userHasRole(user, 'hod')) return [];
  const explicit = user.hodDepartments?.filter(Boolean) ?? [];
  if (explicit.length) return explicit;
  // Best-effort fallback: the department on their teacher record.
  const linked = getLinkedTeacher(user, teachers);
  return linked?.department ? [linked.department] : [];
}

export default {
  ROLES,
  getPermissions,
  hasPermission,
  getLinkedTeacher,
  canEnterMarksFor,
  canEnterClassCommentFor,
  canEnterHodCommentFor,
  canViewClassReports,
  canViewStudentProfile,
  getAccessibleClasses,
  isTermEditable,
};
